import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Collapse,
  FormControlLabel,
  FormGroup,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Rating,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { APP_ICONS } from "../../assets";
import appcolors from "../../constants/Colors";
import {
  BANNER_POSITION,
  CURRENCY,
  ECOMMERCE_SOLUTION_TYPE,
  ORDER_STATUS_TRANSITIONS,
  TAG_APPLIES_TO,
} from "../../constants/Variables";
import { AdminRoutes } from "../../routes";
import { roundTo2Decimal } from "../../utils";
import GlobalButton from "../CustomButton";
import CustomDropDown from "../CutomDropDown";
import IOSSwitch from "../IsoSwitch";

const tableStyle = {
  "&.MuiTableContainer-root .MuiTableHead-root": {
    backgroundColor: appcolors.primary,
  },
  "&.MuiTableContainer-root .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root":
    {
      padding: "6px 16px",
      color: appcolors.white,
      fontSize: "1rem",
    },
  //   "&.MuiTableContainer-root": {
  //     "&::-webkit-scrollbar": {
  //       width: "8px",
  //       height: "8px",
  //     },
  //     "&::-webkit-scrollbar-thumb": {
  //       backgroundColor: appcolors.primary,
  //     },
  //     "&::-webkit-scrollbar-thumb:hover": {
  //       backgroundColor: appcolors.secondary,
  //     },
  //     "&::-webkit-scrollbar-track": {
  //       backgroundColor: appcolors.lightGrey,
  //     },
  //   },
};

export default function GlobalTable({
  tableWidth,
  minWidth,
  tableHeader,
  tableData,
  displayRows,
  showEdit,
  showDelete,
  showActions,
  handleEdit,
  handleDelete,
  showPagination = true,
  handleChangeStatus,
  showDetail,
  handleNavigateToDetail,
  handleChangeOrderStatus,
  showAdminUserPage = false,
  handleResend,
  shippingFee,
  deliveryType,
  handleChangeFeatured,
  handleChangeDiscountStatus,
  handleNavigateToAttributeValue,
  onPopularityChange,
  isBulkMode = false,
  pendingStatusChanges = {},
}) {
  const { t } = useTranslation(["common"]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [meunAnchorEl, setMenuAnchorEl] = useState(null);
  const openAddEditMenu = Boolean(meunAnchorEl);
  const [rowData, setRowData] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const { currentLanguage } = useSelector((state) => state.languageReducer);

  const handleClickAddEditMenu = (event, row, index) => {
    setMenuAnchorEl(event.currentTarget);
    setRowData({ row, index });
  };

  const handleCloseAddEditMenu = () => {
    setMenuAnchorEl(null);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleTogglePagesRow = (rowIndex) => {
    setExpandedRow(expandedRow === rowIndex ? null : rowIndex);
  };

  const orderStatusColors = (status) => {
    if (status === "pending") return "orange";
    if (status === "processing") return "blue";
    if (status === "shipped") return "purple";
    if (status === "delivered") return "green";
    if (status === "cancelled") return "red";
    if (status === "user_cancelled") return "red";
  };

  const renderColorBox = (colorname, color) => {
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          border: "1px solid #ccc",
          borderRadius: 1,
          padding: "4px 8px",
          backgroundColor: "#f9f9f9",
        }}
      >
        <Box
          sx={{
            fontSize: "14px",
            color: "#333",
            fontWeight: 500,
            marginRight: "8px",
          }}
        >
          {colorname}
        </Box>
        <Box
          sx={{
            width: 18,
            height: 18,
            borderRadius: "50%",
            backgroundColor: color,
            border: "1px solid #ddd",
          }}
        ></Box>
      </Stack>
    );
  };

  const PRODUCT_STATUS = [
    {
      label: "Pending",
      value: "pending",
    },
    {
      label: "Processing",
      value: "processing",
    },
    {
      label: "Shipped",
      value: "shipped",
    },
    {
      label: "Delivered",
      value: "delivered",
    },
    {
      label: "Cancelled",
      value: "cancelled",
    },
    // {
    //   label: "User Cancelled",
    //   value: "user_cancelled",
    // },
  ];

  const paginatedTableData =
    rowsPerPage > 0
      ? tableData?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
      : tableData;

  return (
    <>
      <TableContainer sx={tableStyle}>
        <Table
          sx={{
            width: tableWidth ? tableWidth : "100%",
            minWidth: minWidth ? minWidth : "900px",
          }}
        >
          <TableHead>
            <TableRow>
              {tableHeader?.map((header) => (
                <TableCell key={header} sx={{ textTransform: "capitalize" }}>
                  {t(header)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {!paginatedTableData?.length && (
              <TableRow>
                <TableCell colSpan={tableHeader?.length || 1} align="center">
                  {t("noDataFound")}
                </TableCell>
              </TableRow>
            )}
            {paginatedTableData?.map((row, index) => {
              const getVariantLabel = (attributes) => {
                if (!attributes || typeof attributes !== "object") {
                  return "Selected variant";
                }

                const entries = Object.entries(attributes);
                if (entries.length === 0) return "Selected variant";

                return entries
                  .map(
                    ([key, val]) =>
                      `${val?.label?.[currentLanguage] || val?.label || val?.value || "-"}`,
                  )
                  .join(", ");
              };

              const variantLabel = row?.selectedVariant
                ? getVariantLabel(row?.selectedVariant?.attributes)
                : null;

              return (
                <React.Fragment>
                  <TableRow
                    key={index}
                    sx={{
                      backgroundColor:
                        row.type === "Package" ? "#3b384426" : "inherit",
                    }}
                  >
                    {displayRows.map((val) => (
                      <React.Fragment key={val}>
                        {val === "created_date" ? (
                          <TableCell>
                            {moment(row.createdAt).format("DD/MM/YYYY")}
                            <br />
                            {moment(row.createdAt).format("hh:mm:ss a")}
                          </TableCell>
                        ) : val === "category_name" ? (
                          <TableCell style={{ textTransform: "capitalize" }}>
                            {row.name}
                          </TableCell>
                        ) : val === "delivery_type" ? (
                          <TableCell style={{ textTransform: "capitalize" }}>
                            {deliveryType}
                          </TableCell>
                        ) : val === "shipping_fee" ? (
                          <TableCell style={{ textTransform: "capitalize" }}>
                            {shippingFee}
                          </TableCell>
                        ) : val === "active_Icon" ? (
                          <TableCell>
                            <Avatar
                              src={row.activeIcon}
                              sx={{
                                height: 60,
                                width: 60,
                                borderRadius: 2,
                                objectFit: "contain",
                              }}
                            />
                          </TableCell>
                        ) : val === "category_description" ? (
                          <TableCell>
                            <Typography
                              width={300}
                              sx={{
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 2,
                              }}
                            >
                              {row.description[currentLanguage]}
                            </Typography>
                          </TableCell>
                        ) : val === "ecoomerce_category" ? (
                          <TableCell style={{ textTransform: "capitalize" }}>
                            <Stack
                              direction={"row"}
                              gap={1}
                              alignItems={"center"}
                            >
                              <Avatar
                                src={row?.activeIcon}
                                sx={{
                                  height: 60,
                                  width: 60,
                                  borderRadius: 2,
                                }}
                              />
                              <Stack>
                                <Typography variant="body1" fontWeight={600}>
                                  {row.name[currentLanguage]}
                                </Typography>
                                <Typography variant="body2">
                                  {t("totalItems")}: {row?.totalProducts || "0"}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "ecoomerce_sub_category" ? (
                          <TableCell style={{ textTransform: "capitalize" }}>
                            <Stack
                              direction={"row"}
                              gap={1}
                              alignItems={"center"}
                            >
                              <Avatar
                                src={row?.activeIcon}
                                sx={{
                                  height: 60,
                                  width: 60,
                                  borderRadius: 2,
                                }}
                              />
                              <Stack>
                                <Typography variant="body1" fontWeight={600}>
                                  {row.name[currentLanguage]}
                                </Typography>
                                <Typography variant="body1">
                                  {row?.category?.name[currentLanguage]}
                                </Typography>

                                <Typography variant="body2">
                                  {t("totalItems")}: {row?.totalProducts || "0"}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "ecoomerce_sub_sub_category" ? (
                          <TableCell style={{ textTransform: "capitalize" }}>
                            <Stack
                              direction={"row"}
                              gap={1}
                              alignItems={"flex-start"}
                              justifyContent={"flex-start"}
                            >
                              <Avatar
                                src={row?.activeIcon}
                                sx={{
                                  height: 60,
                                  width: 60,
                                  borderRadius: 2,
                                }}
                              />
                              <Stack>
                                <Typography variant="body1" fontWeight={600}>
                                  {row.name[currentLanguage]}
                                </Typography>
                                <Typography variant="body1">
                                  {row?.category?.name[currentLanguage]}
                                </Typography>
                                <Typography variant="body1">
                                  {row?.subCategory?.name[currentLanguage]}
                                </Typography>

                                <Typography variant="body2">
                                  {t("totalItems")}: {row?.totalProducts || "0"}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "ecom_category_description" ? (
                          <TableCell>
                            <Typography width={400}>
                              {row.description[currentLanguage]}
                            </Typography>
                          </TableCell>
                        ) : val === "service_category" ? (
                          <TableCell style={{ textTransform: "capitalize" }}>
                            <Stack
                              direction={"row"}
                              gap={1}
                              alignItems={"center"}
                            >
                              <Avatar
                                src={row.image}
                                sx={{
                                  height: 60,
                                  width: 60,
                                  borderRadius: 2,
                                }}
                              />
                              <Stack>
                                <Typography variant="body1" fontWeight={600}>
                                  {row.name[currentLanguage]}
                                </Typography>
                                <Typography variant="body2">
                                  {t("totalServices")}:{" "}
                                  {row?.subserviceCount || "0"}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "service_sub_category" ? (
                          <TableCell style={{ textTransform: "capitalize" }}>
                            <Stack
                              direction={"row"}
                              gap={1}
                              alignItems={"center"}
                            >
                              <Avatar
                                src={row.image}
                                sx={{
                                  height: 60,
                                  width: 60,
                                  borderRadius: 2,
                                }}
                              />

                              <Typography variant="body1" fontWeight={600}>
                                {row.name[currentLanguage]}
                              </Typography>
                            </Stack>
                          </TableCell>
                        ) : val === "status" ? (
                          <TableCell>
                            <FormGroup
                              sx={{
                                "&.MuiFormGroup-root .MuiFormControlLabel-root":
                                  {
                                    display: "flex",
                                    flexDirection: "column-reverse",
                                    justifyContent: "center",
                                    alignItems: "flex-start",
                                  },
                                "&.MuiFormGroup-root .MuiFormControlLabel-root .MuiFormControlLabel-label ":
                                  {
                                    fontSize: "0.75rem",
                                  },
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <IOSSwitch
                                    defaultChecked
                                    checked={row.status}
                                    onChange={(e) =>
                                      handleChangeStatus(
                                        e.target.checked,
                                        row,
                                        index,
                                      )
                                    }
                                  />
                                }
                                // label={row.status ? t("unBlock") : t("block")}
                              />
                            </FormGroup>
                          </TableCell>
                        ) : val === "featured" ? (
                          <TableCell>
                            <FormGroup
                              sx={{
                                "&.MuiFormGroup-root .MuiFormControlLabel-root":
                                  {
                                    display: "flex",
                                    flexDirection: "column-reverse",
                                    justifyContent: "center",
                                    alignItems: "flex-start",
                                  },
                                "&.MuiFormGroup-root .MuiFormControlLabel-root .MuiFormControlLabel-label ":
                                  {
                                    fontSize: "0.75rem",
                                  },
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <IOSSwitch
                                    defaultChecked
                                    checked={row.isFeatured}
                                    onChange={(e) =>
                                      handleChangeFeatured(
                                        e.target.checked,
                                        row,
                                        index,
                                      )
                                    }
                                  />
                                }
                                // label={row.status ? t("unBlock") : t("block")}
                              />
                            </FormGroup>
                          </TableCell>
                        ) : val === "product_store" ? (
                          <TableCell>
                            {row?.createdBy?.businessName || "Eaziday"}
                          </TableCell>
                        ) : val === "product_name" ? (
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={1}
                              width={"400px"}
                            >
                              <Avatar
                                src={
                                  row?.selectedVariant?.images?.length > 0
                                    ? row?.selectedVariant?.images[0]
                                    : row.productId?.coverImage
                                }
                                sx={{
                                  height: 55,
                                  width: 55,
                                  borderRadius: "6px",
                                  backgroundColor: "#edebeb",
                                  border: "1px solid #edebeb",
                                }}
                              />
                              <Typography
                                variant={"body1"}
                                textTransform={"capitalize"}
                              >
                                {row.productId?.name[currentLanguage]}
                              </Typography>
                            </Stack>
                          </TableCell>
                        ) : val === "product_tax" ? (
                          <TableCell>{row.tax}%</TableCell>
                        ) : val === "product_purchase_price_without_vat" ? (
                          <TableCell>{`${row.pricing?.price} ${CURRENCY}`}</TableCell>
                        ) : val === "product_profit_margin" ? (
                          <TableCell>{`${row.profitMargin}%`}</TableCell>
                        ) : val === "product_discount" ? (
                          <TableCell>{row.discount}</TableCell>
                        ) : val === "product_final_amount" ? (
                          <TableCell>
                            {`${row.pricing?.finalPrice} ${CURRENCY}`}
                          </TableCell>
                        ) : val === "product_final_price" ? (
                          <TableCell>
                            {`${row.pricing?.finalPrice * row.selectedQty} ${CURRENCY}`}
                          </TableCell>
                        ) : val === "product_selected_size" ? (
                          <TableCell>
                            {row?.selectedSize?.name ? (
                              <Chip
                                sx={{ borderRadius: 1 }}
                                label={row?.selectedSize?.name}
                              />
                            ) : row.selectedSizeWithColor ? (
                              <Chip
                                sx={{ borderRadius: 1 }}
                                label={row?.selectedSizeWithColor?.size}
                              />
                            ) : (
                              "_"
                            )}
                          </TableCell>
                        ) : val === "product_selected_color" ? (
                          <TableCell>
                            {row?.selectedColor?.color
                              ? renderColorBox(
                                  row?.selectedColor?.colorName,
                                  row?.selectedColor?.color,
                                )
                              : row.selectedSizeWithColor
                                ? renderColorBox(
                                    row?.selectedSizeWithColor?.colorName,
                                    row?.selectedSizeWithColor?.color,
                                  )
                                : "_"}
                          </TableCell>
                        ) : val === "product_selected_weight" ? (
                          <TableCell>
                            {row?.selectedWeight ? (
                              <Chip
                                sx={{ borderRadius: 1 }}
                                label={row?.selectedWeight?.name}
                              />
                            ) : (
                              "_"
                            )}
                          </TableCell>
                        ) : val === "product_status" ? (
                          <TableCell
                            sx={{
                              width: 200,
                              position: "sticky",
                              right: 0,
                              backgroundColor: "secondary.light",
                              zIndex: 3,
                              minWidth: 50,
                              boxShadow: "inset 4px 0 8px rgba(0,0,0,0.08)",
                            }}
                          >
                            {[
                              "user_cancelled",
                              "cancelled",
                              "delivered",
                            ].includes(row?.orderStatus) &&
                            row?.showDropdown === false ? (
                              <Chip
                                size="small"
                                label={row?.orderStatus?.replace("_", " ")}
                                sx={{
                                  backgroundColor: orderStatusColors(
                                    row.orderStatus,
                                  ),
                                  textTransform: "capitalize",
                                  color: "white",
                                }}
                              />
                            ) : (
                              (() => {
                                // Single source of truth: whichever status is actually being shown
                                // (pending bulk selection, or the saved status) drives BOTH the
                                // dropdown's selected value and its color.
                                const effectiveStatus = isBulkMode
                                  ? (pendingStatusChanges[row._id] ??
                                    row.orderStatus)
                                  : row.orderStatus;

                                return (
                                  <CustomDropDown
                                    sx={{
                                      color: orderStatusColors(effectiveStatus),
                                    }}
                                    value={effectiveStatus}
                                    onChange={(e) =>
                                      handleChangeOrderStatus(
                                        e.target.value,
                                        row,
                                        index,
                                      )
                                    }
                                    formStyleProps={{
                                      "& .MuiSelect-select": {
                                        textTransform: "capitalize",
                                      },
                                    }}
                                  >
                                    {PRODUCT_STATUS.map((val, index) => {
                                      const savedStatus =
                                        row?.savedOrderStatus ??
                                        row?.orderStatus;
                                      // const isReverse =
                                      //   (savedStatus === "processing" &&
                                      //     val.value === "pending") ||
                                      //   (savedStatus === "shipped" &&
                                      //     val.value === "pending") ||
                                      //   (savedStatus === "shipped" &&
                                      //     val.value === "processing") ||
                                      //   (savedStatus === "delivered" &&
                                      //     (val.value === "pending" ||
                                      //       val.value === "processing" ||
                                      //       val.value === "shipped"));

                                      const isReverse =
                                        val.value !== savedStatus &&
                                        !ORDER_STATUS_TRANSITIONS[
                                          savedStatus
                                        ]?.includes(val.value);
                                      return (
                                        <MenuItem
                                          key={index}
                                          value={val.value}
                                          disabled={isReverse}
                                          sx={{
                                            color: orderStatusColors(val.value),
                                            textTransform: "capitalize",
                                          }}
                                        >
                                          {val.label}
                                        </MenuItem>
                                      );
                                    })}
                                  </CustomDropDown>
                                );
                              })()
                            )}
                          </TableCell>
                        ) : val === "product_delivered_at" ? (
                          <TableCell>
                            {row?.deliveredAt ? (
                              <Stack p={0.5}>
                                {moment(row?.deliveredAt).format("DD/MM/YYYYY")}
                              </Stack>
                            ) : (
                              "_"
                            )}
                          </TableCell>
                        ) : val === "role_name" ? (
                          <TableCell style={{ textTransform: "capitalize" }}>
                            {row.role}
                          </TableCell>
                        ) : val === "admin_user_role" ? (
                          <TableCell style={{ textTransform: "capitalize" }}>
                            {row?.role?.role}
                          </TableCell>
                        ) : val === "assigned_pages" ? (
                          <TableCell>
                            <Button
                              startIcon={
                                expandedRow === index ? (
                                  <ExpandLessIcon />
                                ) : (
                                  <ExpandMoreIcon />
                                )
                              }
                              disableRipple
                              variant="none"
                              sx={{
                                width: "auto",
                                "&:hover": { background: "transparent" },
                              }}
                              onClick={() => handleTogglePagesRow(index)}
                            >
                              {expandedRow === index ? t("less") : t("more")}
                            </Button>
                          </TableCell>
                        ) : val === "resend_btn" ? (
                          <TableCell style={{ textTransform: "capitalize" }}>
                            <GlobalButton
                              btnText={"resend"}
                              handleClickBtn={() => handleResend(row)}
                              styleProps={{
                                width: 70,
                                height: 35,
                                borderRadius: "6px",
                                fontSize: "0.875rem",
                              }}
                            />
                          </TableCell>
                        ) : val === "restore_store" ? (
                          <TableCell>
                            {row?.businessName || "--"}
                          </TableCell>
                        ) : val === "reviewed_product_detail" ? (
                          <TableCell>
                            <Stack>
                              <Stack
                                direction={"row"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Avatar
                                  src={row.productImage}
                                  sx={{
                                    height: 55,
                                    width: 55,
                                    borderRadius: "6px",
                                  }}
                                />
                                <Typography variant="body1">
                                  {row.productName[currentLanguage]}
                                </Typography>
                              </Stack>
                            </Stack>
                          </TableCell>
                        ) : val === "user_details_review" ? (
                          <TableCell>
                            <Stack>
                              <Stack
                                direction={"row"}
                                justifyContent={"space-between"}
                              >
                                <Typography
                                  variant="h3"
                                  fontSize={"1rem"}
                                  fontWeight={600}
                                >
                                  {row.username}
                                </Typography>
                                <Rating
                                  value={row.rating}
                                  precision={0.5}
                                  readOnly
                                  size="small"
                                  color="#7922D8"
                                />
                              </Stack>
                              <Typography variant="body2">
                                {row.comment}
                              </Typography>
                            </Stack>
                          </TableCell>
                        ) : val === "ecommerce_tag_name" ? (
                          <TableCell style={{ textTransform: "capitalize" }}>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={1}
                            >
                              <Avatar
                                src={row.icon}
                                sx={{
                                  height: 30,
                                  width: 30,
                                  borderRadius: 1,
                                }}
                              />
                              <Typography variant="body1">
                                {row.name[currentLanguage]}
                              </Typography>
                            </Stack>
                          </TableCell>
                        ) : val === "solution_type" ? (
                          <TableCell>
                            {row.solutionType
                              ?.map(
                                (y) =>
                                  ECOMMERCE_SOLUTION_TYPE.find(
                                    (item) => item.value === y,
                                  )?.label[currentLanguage],
                              )
                              .join(", ")}
                          </TableCell>
                        ) : val === "applies_to" ? (
                          <TableCell>
                            {TAG_APPLIES_TO.find(
                              (item) => item.value === row.appliesTo,
                            )?.label[currentLanguage] || "-"}
                          </TableCell>
                        ) : val === "banner_title" ? (
                          <TableCell>{row.title[currentLanguage]}</TableCell>
                        ) : val === "banner_position" ? (
                          <TableCell>
                            {
                              BANNER_POSITION.find(
                                (item) => item.value === row.position,
                              )?.label[currentLanguage]
                            }
                          </TableCell>
                        ) : val === "banner_link" ? (
                          <TableCell>{row.link ? row.link : "-"}</TableCell>
                        ) : val === "order_product_unit_price" ? (
                          <TableCell>{`${row.pricing?.price} ${CURRENCY}`}</TableCell>
                        ) : val === "order_product_profit_margin" ? (
                          <TableCell>{`${row.pricing?.profitMargin}%`}</TableCell>
                        ) : val === "order_product_discount" ? (
                          <TableCell>{`${row.pricing?.discount}`}</TableCell>
                        ) : val === "order_product_final_price" ? (
                          <TableCell>{`${row.pricing?.finalPrice} ${CURRENCY}`}</TableCell>
                        ) : val === "order_product_vat" ? (
                          <TableCell>{`${row.pricing?.vat}%`}</TableCell>
                        ) : val === "order_product_vat_amount" ? (
                          <TableCell>{`${row.pricing?.originalVatAmount} ${CURRENCY}`}</TableCell>
                        ) : val === "order_product_amount_after_vat" ? (
                          <TableCell>{`${roundTo2Decimal(row.pricing?.originalVatAmount + row.pricing?.finalPrice)} ${CURRENCY}`}</TableCell>
                        ) : val === "order_product_variant" ? (
                          <TableCell>
                            {row?.selectedVariant ? (
                              <Chip
                                sx={{ borderRadius: 1 }}
                                label={variantLabel || "Selected variant"}
                              />
                            ) : (
                              "_"
                            )}
                          </TableCell>
                        ) : val === "order_product_quantity" ? (
                          <TableCell>{`${row.selectedQty}`}</TableCell>
                        ) : val === "attribute_name" ? (
                          <TableCell>{row.label[currentLanguage]}</TableCell>
                        ) : val === "attribute_value_name" ? (
                          <TableCell>{row.label[currentLanguage]}</TableCell>
                        ) : val === "attribute_values" ? (
                          <TableCell>
                            <GlobalButton
                              variant={"contained"}
                              btnText={"addValues"}
                              handleClickBtn={() =>
                                handleNavigateToAttributeValue(row)
                              }
                              styleProps={{
                                width: "auto",
                                height: 40,
                              }}
                            />
                          </TableCell>
                        ) : val === "min_amount" ? (
                          <TableCell>
                            {`${row.minAmount} ${CURRENCY}`}
                          </TableCell>
                        ) : val === "discount_amount" ? (
                          <TableCell>
                            {`${row.discountAmount} ${CURRENCY}`}
                          </TableCell>
                        ) : val === "start_date" ? (
                          <TableCell>
                            {moment(row.startDate).format("DD/MM/YYYY")}
                          </TableCell>
                        ) : val === "end_date" ? (
                          <TableCell>
                            {moment(row.endDate).format("DD/MM/YYYY")}
                          </TableCell>
                        ) : val === "discount_validity" ? (
                          <TableCell>
                            {moment(row.startDate).diff(
                              moment(row.endDate),
                              "days",
                            )}{" "}
                            days
                          </TableCell>
                        ) : val === "discount_status" ? (
                          <TableCell>
                            <FormGroup
                              sx={{
                                "&.MuiFormGroup-root .MuiFormControlLabel-root":
                                  {
                                    display: "flex",
                                    flexDirection: "column-reverse",
                                    justifyContent: "center",
                                    alignItems: "flex-start",
                                  },
                                "&.MuiFormGroup-root .MuiFormControlLabel-root .MuiFormControlLabel-label ":
                                  {
                                    fontSize: "0.75rem",
                                  },
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <IOSSwitch
                                    defaultChecked
                                    checked={row.isActive}
                                    onChange={(e) =>
                                      handleChangeDiscountStatus(
                                        e.target.checked,
                                        row,
                                        index,
                                      )
                                    }
                                  />
                                }
                              />
                            </FormGroup>
                          </TableCell>
                        ) : val === "discount_popularity" ? (
                          <TableCell>
                            <FormGroup
                              sx={{
                                "&.MuiFormGroup-root .MuiFormControlLabel-root":
                                  {
                                    display: "flex",
                                    flexDirection: "column-reverse",
                                    justifyContent: "center",
                                    alignItems: "flex-start",
                                  },
                                "&.MuiFormGroup-root .MuiFormControlLabel-root .MuiFormControlLabel-label ":
                                  {
                                    fontSize: "0.75rem",
                                  },
                              }}
                            >
                              <FormControlLabel
                                control={
                                  <IOSSwitch
                                    defaultChecked
                                    checked={row.isPopular}
                                    onChange={(e) =>
                                      onPopularityChange(
                                        e.target.checked,
                                        row,
                                        index,
                                      )
                                    }
                                  />
                                }
                              />
                            </FormGroup>
                          </TableCell>
                        ) : val === "discount_rule_description" ? (
                          <TableCell>
                            <Typography
                              width={300}
                              sx={{
                                overflow: "hidden",
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 2,
                              }}
                            >
                              {row?.description?.[currentLanguage] || "-"}
                            </Typography>
                          </TableCell>
                        ) : val === "discount_rule_name" ? (
                          <TableCell>
                            {row?.name?.[currentLanguage] || "-"}
                          </TableCell>
                        ) : val === "actions" ? (
                          <TableCell>
                            <Stack
                              gap={1}
                              direction={"row"}
                              alignItems={"center"}
                            >
                              {showDetail && (
                                <IconButton
                                  sx={{ borderRadius: "12px" }}
                                  onClick={() =>
                                    handleNavigateToDetail(row, index)
                                  }
                                >
                                  <Stack>
                                    <img
                                      src={APP_ICONS.rightArrowIcon}
                                      width={30}
                                    />
                                    <Typography variant="body2">
                                      {t("details")}
                                    </Typography>
                                  </Stack>
                                </IconButton>
                              )}
                              {showActions && (
                                <>
                                  <Button
                                    disableRipple
                                    id="addEdit-menu-button"
                                    aria-controls={
                                      openAddEditMenu
                                        ? "addEdit-menu"
                                        : undefined
                                    }
                                    startIcon={
                                      <MoreVertIcon htmlColor="#000000" />
                                    }
                                    aria-haspopup="true"
                                    aria-expanded={
                                      openAddEditMenu ? "true" : undefined
                                    }
                                    sx={{
                                      width: "auto",
                                      padding: "5px",
                                      minWidth: 0,
                                      "& .MuiButton-startIcon": {
                                        mx: 0,
                                      },
                                      "&:hover": {
                                        backgroundColor: "#ffffff",
                                      },
                                    }}
                                    onClick={(e) =>
                                      handleClickAddEditMenu(e, row, index)
                                    }
                                  ></Button>
                                  <Menu
                                    id="addEdit-menu"
                                    anchorEl={meunAnchorEl}
                                    open={openAddEditMenu}
                                    onClose={handleCloseAddEditMenu}
                                    MenuListProps={{
                                      "aria-labelledby": "addEdit-menu-button",
                                    }}
                                    sx={{
                                      ".MuiMenu-paper": {
                                        boxShadow: "none",
                                        border: "1px solid grey",
                                      },
                                    }}
                                  >
                                    <Stack
                                      direction={"column"}
                                      alignItems={"center"}
                                      spacing={1}
                                      paddingX={1}
                                    >
                                      {showEdit && (
                                        <img
                                          src={APP_ICONS.editMustardIcon}
                                          alt="delete-icon"
                                          width={20}
                                          style={{
                                            cursor: "pointer",
                                          }}
                                          onClick={() => {
                                            handleEdit(rowData);
                                            handleCloseAddEditMenu();
                                          }}
                                        />
                                      )}
                                      {showDelete && (
                                        <img
                                          src={APP_ICONS.deleteRedIcon}
                                          alt="delete-icon"
                                          width={20}
                                          style={{
                                            cursor: "pointer",
                                          }}
                                          onClick={() => {
                                            handleDelete(rowData);
                                            handleCloseAddEditMenu();
                                          }}
                                        />
                                      )}
                                    </Stack>
                                  </Menu>
                                </>
                              )}
                            </Stack>
                          </TableCell>
                        ) : (
                          <TableCell>
                            <Typography
                              lineHeight="normal"
                              fontSize={"0.875rem"}
                            >
                              {row[val]}
                            </Typography>
                          </TableCell>
                        )}
                      </React.Fragment>
                    ))}
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={tableHeader.length}
                      style={{ padding: 0, borderBottom: "none" }}
                    >
                      <Collapse
                        in={expandedRow === index}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Paper
                          elevation={2}
                          sx={{ marginBottom: 0, padding: 2 }}
                        >
                          <Typography
                            variant="h1"
                            fontWeight={600}
                            fontSize={"1rem"}
                          >
                            {t("allModules")}
                          </Typography>
                          <List>
                            {AdminRoutes?.slice(0, -1).map((valll, _ind) => {
                              let assigned;
                              if (showAdminUserPage) {
                                assigned = row?.role?.pages?.find(
                                  (it) => it.name === valll.name,
                                );
                              } else {
                                assigned = row.pages?.find(
                                  (it) => it.name === valll.name,
                                );
                              }

                              return (
                                <ListItem>
                                  <ListItemIcon>
                                    <img
                                      src={
                                        assigned !== undefined
                                          ? APP_ICONS.squareCheckGreenIcon
                                          : APP_ICONS.squareGreyIcon
                                      }
                                      alt="check-icon"
                                      width={20}
                                    />
                                  </ListItemIcon>
                                  <ListItemText secondary={valll.name} />
                                </ListItem>
                              );
                            })}
                          </List>
                        </Paper>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      {showPagination && (
        <TablePagination
          rowsPerPageOptions={[10, 20, 30, 50, 100]}
          component="div"
          count={tableData?.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t("rowsPerPage")}
        />
      )}
    </>
  );
}
